import { Mode } from './modes';
import { ChordQuality } from './chords';

export type ChordDegree = 1 | 2 | 3 | 4 | 5 | 6 | 7;

/** Take the diatonic chord at this scale degree of the *selected* mode. */
export interface DiatonicStep {
  kind: 'diatonic';
  degree: ChordDegree;
  quality?: ChordQuality;   // optional extension of the diatonic triad (e.g. 'min7','dom7','maj7'); root stays the diatonic degree's root
  numeral?: string;         // optional explicit numeral when an extension is used (e.g. 'ii7','V7','Imaj7')
}

/** A borrowed / chromatic chord that is NOT in the mode's diatonic set. */
export interface ChromaticStep {
  kind: 'chromatic';
  rootOffset: number;     // semitones above the key tonic, 0..11
  quality: ChordQuality;  // e.g. 'maj' | 'dom7' | 'min' | 'dim'
  numeral: string;        // explicit display numeral, e.g. '♭III', 'V/V', 'V'
}

export type ProgressionStep = DiatonicStep | ChromaticStep;

export interface SongExample {
  title: string;
  artist: string;
  year?: number;
}

export type ProgressionMood =
  | 'happy' | 'sad' | 'tense' | 'triumphant'
  | 'dreamy' | 'melancholic' | 'bluesy' | 'anthemic';
export type ProgressionGenre =
  | 'pop' | 'rock' | 'blues' | 'jazz'
  | 'folk' | 'classical' | 'country' | 'r&b';

export interface Progression {
  id: string;
  name?: string;                // optional nickname; absent → derived numeral string
  steps: ProgressionStep[];
  modes: Mode[];                // modes this progression is idiomatic in
  mood: ProgressionMood[];
  genre: ProgressionGenre[];
  description: string;
  examples: SongExample[];      // 1–3 verified songs (empty for now; filled in a later task)
}

// Helper for diatonic steps in the literal table below.
const diatonic = (degree: ChordDegree): DiatonicStep => ({ kind: 'diatonic', degree });

export const PROGRESSIONS: Progression[] = [
  {
    id: 'i-iv-v',
    name: 'I–IV–V (Three-Chord)',
    steps: [diatonic(1), diatonic(4), diatonic(5)],
    modes: ['ionian'],
    mood: ['happy', 'anthemic'],
    genre: ['rock', 'pop', 'folk', 'country'],
    description: 'The foundational three-chord major progression built on tonic, subdominant and dominant — the backbone of countless rock and folk songs.',
    examples: [
      { title: 'Twist and Shout', artist: 'The Beatles', year: 1963 },
      { title: 'Wild Thing', artist: 'The Troggs', year: 1966 },
      { title: 'La Bamba', artist: 'Ritchie Valens', year: 1958 },
    ],
  },
  {
    id: 'i-v-vi-iv',
    name: 'I–V–vi–IV (Axis)',
    steps: [diatonic(1), diatonic(5), diatonic(6), diatonic(4)],
    modes: ['ionian'],
    mood: ['happy', 'anthemic'],
    genre: ['pop', 'rock'],
    description: 'The ubiquitous \'four-chord song\' axis progression that powers an enormous share of modern pop hits.',
    examples: [
      { title: 'Don\'t Stop Believin\'', artist: 'Journey', year: 1981 },
      { title: 'With or Without You', artist: 'U2', year: 1987 },
      { title: 'Let It Be', artist: 'The Beatles', year: 1970 },
    ],
  },
  {
    id: 'vi-iv-i-v',
    name: 'vi–IV–I–V (Sensitive Female)',
    steps: [diatonic(6), diatonic(4), diatonic(1), diatonic(5)],
    modes: ['ionian'],
    mood: ['melancholic', 'anthemic'],
    genre: ['pop', 'rock'],
    description: 'A rotation of the axis progression starting on the relative minor, lending a bittersweet, emotive lift.',
    examples: [
      { title: 'Apologize', artist: 'OneRepublic', year: 2007 },
      { title: 'Africa', artist: 'Toto', year: 1982 },
    ],
  },
  {
    id: 'i-vi-iv-v',
    name: 'I–vi–IV–V (50s Doo-Wop)',
    steps: [diatonic(1), diatonic(6), diatonic(4), diatonic(5)],
    modes: ['ionian'],
    mood: ['happy', 'dreamy'],
    genre: ['pop', 'r&b', 'rock'],
    description: 'The classic doo-wop \'ice cream changes\' that defined 1950s ballads and remains a nostalgic staple.',
    examples: [
      { title: 'Stand by Me', artist: 'Ben E. King', year: 1961 },
      { title: 'Every Breath You Take', artist: 'The Police', year: 1983 },
      { title: 'Earth Angel', artist: 'The Penguins', year: 1954 },
    ],
  },
  {
    id: 'i-vi-ii-v',
    name: 'I–vi–ii–V (Turnaround)',
    steps: [{ kind: 'diatonic', degree: 1, quality: 'maj7', numeral: 'Imaj7' }, { kind: 'diatonic', degree: 6, quality: 'min7', numeral: 'vi7' }, { kind: 'diatonic', degree: 2, quality: 'min7', numeral: 'ii7' }, { kind: 'diatonic', degree: 5, quality: 'dom7', numeral: 'V7' }],
    modes: ['ionian'],
    mood: ['happy', 'dreamy'],
    genre: ['jazz', 'pop'],
    description: 'The jazz turnaround at the heart of \'rhythm changes\', cycling smoothly back to the tonic through the ii–V.',
    examples: [
      { title: 'I Got Rhythm', artist: 'George Gershwin', year: 1930 },
      { title: 'Heart and Soul', artist: 'Hoagy Carmichael', year: 1938 },
    ],
  },
  {
    id: 'ii-v-i-major',
    name: 'ii–V–I (Major)',
    steps: [{ kind: 'diatonic', degree: 2, quality: 'min7', numeral: 'ii7' }, { kind: 'diatonic', degree: 5, quality: 'dom7', numeral: 'V7' }, { kind: 'diatonic', degree: 1, quality: 'maj7', numeral: 'Imaj7' }],
    modes: ['ionian'],
    mood: ['dreamy', 'happy'],
    genre: ['jazz'],
    description: 'The single most important cadential cell in jazz harmony, resolving the supertonic and dominant into the major tonic.',
    examples: [
      { title: 'Autumn Leaves', artist: 'Joseph Kosma', year: 1945 },
      { title: 'Tune Up', artist: 'Miles Davis', year: 1953 },
    ],
  },
  {
    id: 'i-v-of-v-v',
    name: 'I–V/V–V (Secondary Dominant)',
    steps: [diatonic(1), { kind: 'chromatic', rootOffset: 2, quality: 'dom7', numeral: 'V7/V' }, diatonic(5)],
    modes: ['ionian'],
    mood: ['triumphant', 'happy'],
    genre: ['pop', 'rock', 'classical'],
    description: 'Tonicizes the dominant with a major II (secondary dominant) for a bright, lifting pull into V.',
    examples: [
      { title: 'Don\'t Think Twice, It\'s All Right', artist: 'Bob Dylan', year: 1963 },
    ],
  },
  {
    id: 'i-bvii-iv-i',
    name: 'I–bVII–IV–I (Mixolydian Borrow)',
    steps: [diatonic(1), { kind: 'chromatic', rootOffset: 10, quality: 'maj', numeral: '♭VII' }, diatonic(4), diatonic(1)],
    modes: ['ionian', 'mixolydian'],
    mood: ['anthemic', 'triumphant'],
    genre: ['rock'],
    description: 'A major-key progression that borrows the flat-seventh from Mixolydian for a rootsy, classic-rock cadence.',
    examples: [
      { title: 'Sweet Home Alabama', artist: 'Lynyrd Skynyrd', year: 1974 },
      { title: 'Sympathy for the Devil', artist: 'The Rolling Stones', year: 1968 },
    ],
  },
  {
    id: 'dorian-i-IV-vamp',
    name: 'Dorian i–IV Vamp',
    steps: [{ kind: 'diatonic', degree: 1, quality: 'min7', numeral: 'i7' }, diatonic(4)],
    modes: ['dorian'],
    mood: ['dreamy', 'bluesy'],
    genre: ['rock', 'jazz', 'r&b'],
    description: 'The signature Dorian sound: a minor tonic oscillating with a bright major IV, whose major-sixth scale degree is the mode\'s defining color.',
    examples: [
      { title: 'Oye Como Va', artist: 'Santana', year: 1970 },
      { title: 'Evil Ways', artist: 'Santana', year: 1969 },
    ],
  },
  {
    id: 'dorian-i7-ii7-i7',
    name: 'Dorian i7–ii7 Groove',
    steps: [{ kind: 'diatonic', degree: 1, quality: 'min7', numeral: 'i7' }, { kind: 'diatonic', degree: 2, quality: 'min7', numeral: 'ii7' }],
    modes: ['dorian'],
    mood: ['dreamy', 'bluesy'],
    genre: ['r&b', 'jazz'],
    description: 'A smooth two-chord funk/neo-soul vamp between adjacent minor-seven chords, the parallel min7 ii being a giveaway of Dorian color.',
    examples: [
      { title: 'Moondance', artist: 'Van Morrison', year: 1970 },
    ],
  },
  {
    id: 'phrygian-i-bii',
    name: 'i–♭II',
    steps: [diatonic(1), diatonic(2)],
    modes: ['phrygian'],
    mood: ['tense', 'melancholic'],
    genre: ['rock', 'classical'],
    description: 'The two-chord nucleus of the whole mode: the minor tonic and the major chord a half-step above it, whose semitone clash is the unmistakable Phrygian signature.',
    examples: [
      { title: 'Wherever I May Roam', artist: 'Metallica', year: 1991 },
    ],
  },
  {
    id: 'phrygian-i-bvii-bvi-bii',
    name: 'i–♭VII–♭VI–♭II',
    steps: [diatonic(1), diatonic(7), diatonic(6), diatonic(2)],
    modes: ['phrygian'],
    mood: ['tense', 'melancholic'],
    genre: ['rock', 'classical'],
    description: 'A brooding stepwise descent that bottoms out on the flat-second before resolving, the most cinematic of the full Phrygian progressions.',
    examples: [],
  },
  {
    id: 'phrygian-i-biii-bii',
    name: 'i–♭III–♭II',
    steps: [diatonic(1), diatonic(3), diatonic(2)],
    modes: ['phrygian'],
    mood: ['melancholic', 'tense'],
    genre: ['rock', 'classical'],
    description: 'Rises to the relative-major flat-third then steps down through the flat-second back to the tonic, a compact arc rich in Phrygian flavour.',
    examples: [],
  },
  {
    id: 'phrygian-i-bii-biii-bii',
    name: 'i–♭II–♭III–♭II',
    steps: [diatonic(1), diatonic(2), diatonic(3), diatonic(2)],
    modes: ['phrygian'],
    mood: ['tense', 'melancholic'],
    genre: ['rock', 'classical'],
    description: 'An undulating vamp around the flat-second that keeps the Phrygian half-step constantly in the ear without ever fully releasing.',
    examples: [],
  },
  {
    id: 'phrygian-i-iv-bii',
    name: 'i–iv–♭II',
    steps: [diatonic(1), diatonic(4), diatonic(2)],
    modes: ['phrygian'],
    mood: ['sad', 'tense'],
    genre: ['rock', 'folk'],
    description: 'The minor subdominant gives this a yearning, modal-folk weight before the flat-second pulls everything back to the tonic.',
    examples: [],
  },
  {
    id: 'phrygian-i-bii-bvii',
    name: 'i–♭II–♭VII',
    steps: [diatonic(1), diatonic(2), diatonic(7)],
    modes: ['phrygian'],
    mood: ['tense', 'melancholic'],
    genre: ['rock', 'jazz'],
    description: 'Pairs the signature flat-second with the minor subtonic for a restless, unresolved loop that never settles into major brightness.',
    examples: [],
  },
  {
    id: 'phrygian-bii-i-cadence',
    name: '♭II–i (Phrygian cadence resolution)',
    steps: [diatonic(2), diatonic(1)],
    modes: ['phrygian'],
    mood: ['tense', 'melancholic'],
    genre: ['classical', 'rock'],
    description: 'The half-step fall from the major flat-second down onto the minor tonic, the cadential gesture that defines the Phrygian sound.',
    examples: [],
  },
  {
    id: 'phrygian-i-bvi-bii-bvii',
    name: 'i–♭VI–♭II–♭VII',
    steps: [diatonic(1), diatonic(6), diatonic(2), diatonic(7)],
    modes: ['phrygian'],
    mood: ['tense', 'anthemic'],
    genre: ['rock'],
    description: 'A four-chord Phrygian circuit that contrasts the bright flat-sixth and flat-second majors against the minor tonic and subtonic for an epic, dark-anthem feel.',
    examples: [],
  },
  {
    id: 'lydian-i-ii-vamp',
    name: 'Lydian I–II Vamp',
    steps: [diatonic(1), diatonic(2)],
    modes: ['lydian'],
    mood: ['dreamy', 'happy'],
    genre: ['rock', 'pop'],
    description: 'The quintessential Lydian sound: a static oscillation between the tonic and the bright major II chord built on the raised fourth, instantly evoking wonder.',
    examples: [
      { title: 'Man on the Moon', artist: 'R.E.M.', year: 1992 },
      { title: 'Dreams', artist: 'Fleetwood Mac', year: 1977 },
    ],
  },
  {
    id: 'lydian-i-ii-iii-ii',
    name: 'Lydian I–II–iii–II',
    steps: [diatonic(1), diatonic(2), diatonic(3), diatonic(2)],
    modes: ['lydian'],
    mood: ['dreamy', 'happy'],
    genre: ['rock', 'pop'],
    description: 'Adds the diatonic iii minor as upper colour while the bright II keeps pulling the ear, an arc that climbs then settles back into the Lydian glow.',
    examples: [],
  },
  {
    id: 'lydian-i-ii-vi-v',
    name: 'Lydian I–II–vi–V',
    steps: [diatonic(1), diatonic(2), diatonic(6), diatonic(5)],
    modes: ['lydian'],
    mood: ['dreamy', 'melancholic'],
    genre: ['pop', 'rock'],
    description: 'Pairs the lifting II with the diatonic vi to add a wistful turn, a full four-bar loop that keeps the Lydian brightness without becoming saccharine.',
    examples: [],
  },
  {
    id: 'lydian-i-iii-ii',
    name: 'Lydian I–iii–II',
    steps: [diatonic(1), diatonic(3), diatonic(2)],
    modes: ['lydian'],
    mood: ['dreamy', 'melancholic'],
    genre: ['rock', 'folk'],
    description: 'Drops to the minor iii for a brief shadow before the bright II lifts the ear back up, a gentle ebb-and-flow that highlights the mode\'s bittersweet edge.',
    examples: [],
  },
  {
    id: 'lydian-i-ii-iv-sharp-dim',
    name: 'Lydian I–II–#iv°',
    steps: [diatonic(1), diatonic(2), diatonic(4)],
    modes: ['lydian'],
    mood: ['tense', 'dreamy'],
    genre: ['classical', 'jazz'],
    description: 'Foregrounds the rare diatonic diminished triad on the raised fourth, the most distinctive and unstable chord unique to the Lydian mode, for an eerie shimmer.',
    examples: [],
  },
  {
    id: 'lydian-i-ii-i-vi',
    name: 'Lydian I–II–I–vi',
    steps: [diatonic(1), diatonic(2), diatonic(1), diatonic(6)],
    modes: ['lydian'],
    mood: ['dreamy', 'happy'],
    genre: ['pop', 'rock'],
    description: 'Returns to tonic between excursions so the II repeatedly re-colours the home chord, with the closing vi adding a soft minor sigh to the cycle.',
    examples: [],
  },
  {
    id: 'mixolydian-i-bvii-iv',
    name: 'I-bVII-IV (Mixolydian Vamp)',
    steps: [diatonic(1), diatonic(7), diatonic(4)],
    modes: ['mixolydian'],
    mood: ['anthemic', 'triumphant'],
    genre: ['rock', 'pop'],
    description: 'The signature Mixolydian loop where the major bVII pulls down to IV before resolving back to I, giving a bright yet earthy rock anthem feel.',
    examples: [
      { title: 'Sweet Child o\' Mine', artist: 'Guns N\' Roses', year: 1987 },
      { title: 'Sympathy for the Devil', artist: 'The Rolling Stones', year: 1968 },
    ],
  },
  {
    id: 'mixolydian-bvii-iv-i-double-plagal',
    name: 'bVII-IV-I (Double Plagal Cadence)',
    steps: [diatonic(7), diatonic(4), diatonic(1)],
    modes: ['mixolydian'],
    mood: ['triumphant', 'anthemic'],
    genre: ['rock', 'folk'],
    description: 'Two stacked plagal motions (bVII-IV then IV-I) create a grand, resolving cadence beloved in classic rock outros.',
    examples: [
      { title: 'Hey Jude (coda)', artist: 'The Beatles', year: 1968 },
    ],
  },
  {
    id: 'mixolydian-i-v-bvii-iv',
    name: 'I-v-bVII-IV',
    steps: [diatonic(1), diatonic(5), diatonic(7), diatonic(4)],
    modes: ['mixolydian'],
    mood: ['bluesy', 'melancholic'],
    genre: ['rock', 'folk'],
    description: 'The minor v chord is the giveaway of true Mixolydian, lending a wistful turn before the bVII-IV cadence brightens things again.',
    examples: [
      { title: 'Cinnamon Girl', artist: 'Neil Young', year: 1969 },
    ],
  },
  {
    id: 'mixolydian-i-bvii-vamp',
    name: 'I-bVII Vamp',
    steps: [diatonic(1), diatonic(7)],
    modes: ['mixolydian'],
    mood: ['bluesy', 'anthemic'],
    genre: ['rock', 'blues'],
    description: 'A hypnotic two-chord oscillation between I and the major subtonic that drives countless riff-based rock songs.',
    examples: [
      { title: 'Cinnamon Girl', artist: 'Neil Young', year: 1969 },
      { title: 'Born on the Bayou', artist: 'Creedence Clearwater Revival', year: 1969 },
    ],
  },
  {
    id: 'mixolydian-i7-iv-funk',
    name: 'I7-IV (Dominant Funk Vamp)',
    steps: [{ kind: 'diatonic', degree: 1, quality: 'dom7', numeral: 'I7' }, diatonic(4)],
    modes: ['mixolydian'],
    mood: ['bluesy', 'happy'],
    genre: ['r&b', 'blues'],
    description: 'A dominant-7th tonic vamping to IV is the harmonic bedrock of funk and blues grooves, where the flat 7 lives in the tonic chord itself.',
    examples: [
      { title: 'Cissy Strut', artist: 'The Meters', year: 1969 },
      { title: 'Thank You (Falettinme Be Mice Elf Agin)', artist: 'Sly & The Family Stone', year: 1969 },
    ],
  },
  {
    id: 'mixolydian-i-bvii-iv-i-loop',
    name: 'I-bVII-IV-I (Resolving Loop)',
    steps: [diatonic(1), diatonic(7), diatonic(4), diatonic(1)],
    modes: ['mixolydian'],
    mood: ['anthemic', 'triumphant'],
    genre: ['rock', 'country'],
    description: 'The full four-bar Mixolydian phrase that departs and returns home, a staple of southern rock and jam-band verses.',
    examples: [
      { title: 'Can\'t You See', artist: 'The Marshall Tucker Band', year: 1973 },
    ],
  },
  {
    id: 'mixolydian-i-v-iv',
    name: 'I-v-IV',
    steps: [diatonic(1), diatonic(5), diatonic(4)],
    modes: ['mixolydian'],
    mood: ['melancholic', 'bluesy'],
    genre: ['folk', 'rock'],
    description: 'Pairs the modal minor v directly against major I and IV, a darker Mixolydian flavour common in British folk-rock.',
    examples: [
      { title: 'Cinnamon Girl', artist: 'Neil Young', year: 1969 },
    ],
  },
  {
    id: 'aeolian-i-bvi-biii-bvii',
    name: 'i–bVI–bIII–bVII',
    steps: [diatonic(1), diatonic(6), diatonic(3), diatonic(7)],
    modes: ['aeolian'],
    mood: ['melancholic', 'anthemic'],
    genre: ['pop', 'rock'],
    description: 'The \'sensitive female chord progression\' / minor pop loop, cycling through all three flat majors for an emotive, epic minor-key sweep.',
    examples: [
      { title: 'Numb', artist: 'Linkin Park', year: 2003 },
      { title: 'Save Tonight', artist: 'Eagle-Eye Cherry', year: 1997 },
    ],
  },
  {
    id: 'aeolian-i-bvii-bvi-v',
    name: 'i–bVII–bVI–V (Andalusian)',
    steps: [diatonic(1), diatonic(7), diatonic(6), { kind: 'chromatic', rootOffset: 7, quality: 'maj', numeral: 'V' }],
    modes: ['aeolian'],
    mood: ['tense', 'melancholic'],
    genre: ['folk', 'rock'],
    description: 'The Andalusian cadence, a descending tetrachord whose final major V (a borrowed dominant from harmonic minor) gives a dramatic Spanish-flavored tension before looping back.',
    examples: [
      { title: 'Hit the Road Jack', artist: 'Ray Charles', year: 1961 },
      { title: 'Sultans of Swing', artist: 'Dire Straits', year: 1978 },
    ],
  },
  {
    id: 'aeolian-bvi-bvii-i',
    name: 'bVI–bVII–i (epic minor cadence)',
    steps: [diatonic(6), diatonic(7), diatonic(1), diatonic(1)],
    modes: ['aeolian'],
    mood: ['triumphant', 'anthemic'],
    genre: ['rock', 'classical'],
    description: 'The cinematic \'epic\' cadence where the two flat majors step up to a powerful minor tonic, ubiquitous in film and metal for a heroic, dark resolution.',
    examples: [],
  },
  {
    id: 'r2-dorian-i-bvii-iv',
    name: 'i–♭VII–IV',
    steps: [diatonic(1), diatonic(7), diatonic(4)],
    modes: ['dorian'],
    mood: ['dreamy', 'bluesy'],
    genre: ['rock', 'folk'],
    description: 'A bright Dorian loop where the minor tonic falls to the ♭VII and then lifts into the signature major IV, whose natural 6th gives the mode its hopeful, open colour.',
    examples: [
      { title: 'Wicked Game', artist: 'Chris Isaak', year: 1989 },
    ],
  },
  {
    id: 'r2-dorian-i-v-iv',
    name: 'i–v–IV',
    steps: [diatonic(1), diatonic(5), diatonic(4)],
    modes: ['dorian'],
    mood: ['melancholic', 'dreamy'],
    genre: ['rock', 'folk'],
    description: 'The minor v keeps the harmony modal rather than tonal, and its lack of a leading tone makes the move down to the major IV feel suspended and pensive.',
    examples: [],
  },
  {
    id: 'r2-dorian-i-ii-bvii',
    name: 'i–ii–♭VII',
    steps: [diatonic(1), diatonic(2), diatonic(7)],
    modes: ['dorian'],
    mood: ['melancholic', 'dreamy'],
    genre: ['folk', 'rock'],
    description: 'The bright minor ii (a Dorian hallmark, since natural minor offers a diminished ii°) leans the phrase upward before the ♭VII pulls it gently back to the tonic.',
    examples: [],
  },
  {
    id: 'r2-dorian-i-iv-v-bvii',
    name: 'i–IV–v–♭VII',
    steps: [diatonic(1), diatonic(4), diatonic(5), diatonic(7)],
    modes: ['dorian'],
    mood: ['dreamy', 'anthemic'],
    genre: ['rock', 'pop'],
    description: 'A fuller Dorian circuit pairing the signature major IV with the modal minor v, then closing on the ♭VII, balancing the mode\'s brightness against its rootless, floating cadence.',
    examples: [],
  },
  {
    id: 'r2-aeolian-i-bVII-bVI',
    name: 'i–♭VII–♭VI (Aeolian descent)',
    steps: [diatonic(1), diatonic(7), diatonic(6)],
    modes: ['aeolian'],
    mood: ['melancholic', 'anthemic'],
    genre: ['rock', 'pop'],
    description: 'A three-chord stepwise descent from the minor tonic through the flat-seven to the flat-six, one of the most recognizable minor-key rock loops.',
    examples: [
      { title: 'Stairway to Heaven', artist: 'Led Zeppelin', year: 1971 },
    ],
  },
  {
    id: 'r2-pachelbel-canon',
    name: 'Pachelbel / Canon',
    steps: [diatonic(1), diatonic(5), diatonic(6), diatonic(3), diatonic(4), diatonic(1), diatonic(4), diatonic(5)],
    modes: ['ionian'],
    mood: ['triumphant', 'happy', 'dreamy'],
    genre: ['classical', 'pop', 'rock'],
    description: 'The descending Pachelbel ground bass cycling I-V-vi-iii-IV-I-IV-V, an eight-chord loop underpinning countless pop ballads.',
    examples: [
      { title: 'Canon in D', artist: 'Johann Pachelbel', year: 1680 },
      { title: 'Basket Case', artist: 'Green Day', year: 1994 },
      { title: 'Graduation (Friends Forever)', artist: 'Vitamin C', year: 2000 },
    ],
  },
  {
    id: 'r2-i-iii-iv-v',
    name: 'I-iii-IV-V',
    steps: [diatonic(1), diatonic(3), diatonic(4), diatonic(5)],
    modes: ['ionian'],
    mood: ['happy', 'dreamy', 'triumphant'],
    genre: ['pop', 'rock', 'folk'],
    description: 'A bright stepwise rise from tonic through the mediant to the IV-V cadence, giving a gently uplifting, doo-wop-adjacent lift.',
    examples: [],
  },
  {
    id: 'r2-iv-v-iii-vi-jpop-royal-road',
    name: 'J-Pop Royal Road (IV-V-iii-vi)',
    steps: [diatonic(4), diatonic(5), diatonic(3), diatonic(6)],
    modes: ['ionian'],
    mood: ['melancholic', 'dreamy', 'triumphant'],
    genre: ['pop', 'rock'],
    description: 'The signature Japanese \'royal road\' loop that drives from a IV-V launch through iii to land on the relative-minor vi, prized for its bittersweet sweep.',
    examples: [
      { title: 'Pretender', artist: 'Official Hige Dandism', year: 2019 },
    ],
  },
  {
    id: 'r2-creep-i-iii-iv-iv-minor',
    name: 'Creep (I-III-IV-iv)',
    steps: [diatonic(1), { kind: 'chromatic', rootOffset: 4, quality: 'maj', numeral: 'III' }, diatonic(4), { kind: 'chromatic', rootOffset: 5, quality: 'min', numeral: 'iv' }],
    modes: ['ionian'],
    mood: ['melancholic', 'tense', 'sad'],
    genre: ['rock', 'pop'],
    description: 'The aching \'Creep\' loop using a chromatic secondary-dominant III (V/vi) and a borrowed minor-iv that darkens the IV for a yearning resolution back to I.',
    examples: [
      { title: 'Creep', artist: 'Radiohead', year: 1992 },
    ],
  },
  {
    id: 'r2-iii-vi-ii-v-turnaround',
    name: 'iii7–vi7–ii7–V7 Turnaround',
    steps: [{ kind: 'diatonic', degree: 3, quality: 'min7', numeral: 'iii7' }, { kind: 'diatonic', degree: 6, quality: 'min7', numeral: 'vi7' }, { kind: 'diatonic', degree: 2, quality: 'min7', numeral: 'ii7' }, { kind: 'diatonic', degree: 5, quality: 'dom7', numeral: 'V7' }],
    modes: ['ionian'],
    mood: ['dreamy', 'happy'],
    genre: ['jazz'],
    description: 'A descending-fifths turnaround that substitutes iii7 for Imaj7, cycling smoothly back to the top of a tune through the diatonic chords.',
    examples: [
      { title: 'I Got Rhythm', artist: 'George Gershwin', year: 1930 },
    ],
  },
  {
    id: 'r2-rhythm-changes-bridge',
    name: 'Rhythm Changes Bridge III7-VI7-II7-V7',
    steps: [{ kind: 'chromatic', rootOffset: 4, quality: 'dom7', numeral: 'III7' }, { kind: 'chromatic', rootOffset: 9, quality: 'dom7', numeral: 'VI7' }, { kind: 'chromatic', rootOffset: 2, quality: 'dom7', numeral: 'II7' }, { kind: 'diatonic', degree: 5, quality: 'dom7', numeral: 'V7' }],
    modes: ['ionian'],
    mood: ['tense', 'bluesy', 'triumphant'],
    genre: ['jazz'],
    description: 'The B-section of rhythm changes: a chain of dominant sevenths each resolving down a fifth (secondary dominants) that ratchets tension back to the home key.',
    examples: [
      { title: 'I Got Rhythm', artist: 'George Gershwin', year: 1930 },
      { title: 'Oleo', artist: 'Sonny Rollins', year: 1954 },
      { title: 'Anthropology', artist: 'Charlie Parker', year: 1945 },
    ],
  },
  {
    id: 'r2-backdoor-ii-bvii7-i',
    name: 'Backdoor iv7–♭VII7–Imaj7',
    steps: [{ kind: 'chromatic', rootOffset: 5, quality: 'min7', numeral: 'iv7' }, { kind: 'chromatic', rootOffset: 10, quality: 'dom7', numeral: '♭VII7' }, { kind: 'diatonic', degree: 1, quality: 'maj7', numeral: 'Imaj7' }],
    modes: ['ionian'],
    mood: ['dreamy', 'bluesy', 'melancholic'],
    genre: ['jazz'],
    description: 'The backdoor cadence: a borrowed minor-iv and ♭VII7 (from the parallel minor) slip into the major tonic from a flat-side approach instead of the usual V7.',
    examples: [
      { title: 'Lady Bird', artist: 'Tadd Dameron', year: 1948 },
    ],
  },
  {
    id: 'r2-blues-quick-change',
    name: '12-Bar Quick-Change Blues',
    steps: [{ kind: 'diatonic', degree: 1, quality: 'dom7', numeral: 'I7' }, { kind: 'diatonic', degree: 4, quality: 'dom7', numeral: 'IV7' }, { kind: 'diatonic', degree: 1, quality: 'dom7', numeral: 'I7' }, { kind: 'diatonic', degree: 1, quality: 'dom7', numeral: 'I7' }, { kind: 'diatonic', degree: 4, quality: 'dom7', numeral: 'IV7' }, { kind: 'diatonic', degree: 4, quality: 'dom7', numeral: 'IV7' }, { kind: 'diatonic', degree: 1, quality: 'dom7', numeral: 'I7' }, { kind: 'diatonic', degree: 1, quality: 'dom7', numeral: 'I7' }, { kind: 'diatonic', degree: 5, quality: 'dom7', numeral: 'V7' }, { kind: 'diatonic', degree: 4, quality: 'dom7', numeral: 'IV7' }, { kind: 'diatonic', degree: 1, quality: 'dom7', numeral: 'I7' }, { kind: 'diatonic', degree: 5, quality: 'dom7', numeral: 'V7' }],
    modes: ['mixolydian'],
    mood: ['bluesy', 'happy'],
    genre: ['blues', 'rock'],
    description: 'The standard 12-bar blues with a \'quick change\' to the IV7 in bar 2 before returning to the I7, all on dominant-seventh chords with a turnaround.',
    examples: [
      { title: 'Sweet Home Chicago', artist: 'Robert Johnson', year: 1936 },
      { title: 'Pride and Joy', artist: 'Stevie Ray Vaughan', year: 1983 },
    ],
  },
  {
    id: 'r2-minor-blues',
    name: 'Minor Blues',
    steps: [{ kind: 'diatonic', degree: 1, quality: 'min7', numeral: 'i7' }, { kind: 'diatonic', degree: 4, quality: 'min7', numeral: 'iv7' }, { kind: 'diatonic', degree: 1, quality: 'min7', numeral: 'i7' }, { kind: 'diatonic', degree: 1, quality: 'min7', numeral: 'i7' }, { kind: 'diatonic', degree: 4, quality: 'min7', numeral: 'iv7' }, { kind: 'diatonic', degree: 4, quality: 'min7', numeral: 'iv7' }, { kind: 'diatonic', degree: 1, quality: 'min7', numeral: 'i7' }, { kind: 'diatonic', degree: 1, quality: 'min7', numeral: 'i7' }, { kind: 'chromatic', rootOffset: 8, quality: 'dom7', numeral: '♭VI7' }, { kind: 'chromatic', rootOffset: 7, quality: 'dom7', numeral: 'V7' }, { kind: 'diatonic', degree: 1, quality: 'min7', numeral: 'i7' }, { kind: 'chromatic', rootOffset: 7, quality: 'dom7', numeral: 'V7' }],
    modes: ['aeolian'],
    mood: ['bluesy', 'melancholic', 'sad'],
    genre: ['blues', 'jazz'],
    description: 'A 12-bar minor blues built on minor-seventh i and iv chords with a ♭VI7–V7 turnaround, both the ♭VI7 (a chromatically altered dominant functioning as a tritone substitute) and the V7 (borrowed from harmonic minor) pulling tensely back to the tonic for a dark, blues-jazz feel.',
    examples: [
      { title: 'The Thrill Is Gone', artist: 'B.B. King', year: 1969 },
    ],
  },
  {
    id: 'r2-eight-bar-blues',
    name: '8-Bar Blues',
    steps: [{ kind: 'diatonic', degree: 1, quality: 'dom7', numeral: 'I7' }, { kind: 'diatonic', degree: 5, quality: 'dom7', numeral: 'V7' }, { kind: 'diatonic', degree: 4, quality: 'dom7', numeral: 'IV7' }, { kind: 'diatonic', degree: 4, quality: 'dom7', numeral: 'IV7' }, { kind: 'diatonic', degree: 1, quality: 'dom7', numeral: 'I7' }, { kind: 'diatonic', degree: 5, quality: 'dom7', numeral: 'V7' }, { kind: 'diatonic', degree: 1, quality: 'dom7', numeral: 'I7' }, { kind: 'diatonic', degree: 5, quality: 'dom7', numeral: 'V7' }],
    modes: ['mixolydian'],
    mood: ['bluesy', 'happy'],
    genre: ['blues', 'folk'],
    description: 'A compact eight-bar blues form moving I–V–IV–IV–I–V–I–V, half the length of the standard form and common in early country-blues and folk.',
    examples: [
      { title: 'Key to the Highway', artist: 'Big Bill Broonzy', year: 1941 },
    ],
  },
  {
    id: 'i-iv-vi-v',
    name: 'I–IV–vi–V',
    steps: [diatonic(1), diatonic(4), diatonic(6), diatonic(5)],
    modes: ['ionian'],
    mood: ['happy', 'anthemic'],
    genre: ['pop'],
    description: 'A crowd-pleasing pop progression that swaps the positions of IV and vi for a fresh lift before resolving to V.',
    examples: [],
  },
  {
    id: '12-bar-blues',
    name: '12-Bar Blues',
    steps: [diatonic(1), diatonic(1), diatonic(1), diatonic(1), diatonic(4), diatonic(4), diatonic(1), diatonic(1), diatonic(5), diatonic(4), diatonic(1), diatonic(5)],
    modes: ['ionian'],
    mood: ['bluesy'],
    genre: ['blues', 'rock', 'r&b'],
    description: 'The foundation of the blues idiom. Twelve bars of harmonic drama built from just three chords — I, IV and V.',
    examples: [
      { title: 'Johnny B. Goode', artist: 'Chuck Berry', year: 1958 },
      { title: 'Hound Dog', artist: 'Elvis Presley', year: 1956 },
    ],
  },
  {
    id: 'i-v-iv',
    name: 'I–V–IV',
    steps: [diatonic(1), diatonic(5), diatonic(4)],
    modes: ['ionian'],
    mood: ['triumphant', 'anthemic'],
    genre: ['rock'],
    description: 'Reversing IV and V adds a bluesy swagger. Common in hard rock and classic rock anthems — the dominant resolves downward.',
    examples: [],
  },
  {
    id: 'i-vii-vi-vii-minor',
    name: 'i–VII–VI–VII',
    steps: [diatonic(1), diatonic(7), diatonic(6), diatonic(7)],
    modes: ['aeolian'],
    mood: ['tense', 'melancholic'],
    genre: ['rock'],
    description: 'A natural minor vamp that alternates the tonic with the subtonic and submediant, creating a dark, driving feel.',
    examples: [
      { title: 'All Along the Watchtower', artist: 'Bob Dylan', year: 1967 },
    ],
  },
  {
    id: 'i-iv-v-minor',
    name: 'i–iv–v',
    steps: [diatonic(1), diatonic(4), diatonic(5)],
    modes: ['aeolian'],
    mood: ['sad', 'melancholic'],
    genre: ['folk', 'blues'],
    description: 'The all-minor version of I–IV–V. Darker and more yearning, widely used in folk ballads and blues in minor keys.',
    examples: [],
  },
];
